/**
 * UPA / HOF / Dunk sportsbook — team-based matchups + working bet slip.
 *
 * Integrated into the REAL PrymeBet app (wraps `Shell`, reuses `sb.jsx`
 * primitives + app tokens). Adds a functional slip that posts to the flag-gated
 * `/api/sportsbook-upa/wager` endpoint (authoritative wallet debit) and a My
 * Bets panel from `/api/sportsbook-upa/my-bets`. Renders team context only —
 * never a "confirmed lineup" for HOF/Dunk — with labelled series/game records
 * and pregame-window states. Mounted only when REACT_APP_UPA_SB_ENABLED is set.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Shell } from "@/components/shell/Shell";
import api from "@/lib/api";
import { SBTag, initials } from "@/components/sportsbook/sb";

const EVENT_ID = "evt-hof-1";
const DEMO_USER = "preview-user";

const SYNTHETIC = {
  synthetic: true,
  eventTitle: "Hall Of Fame League · S1",
  matchups: [
    {
      cgid: "hof_dunk_api:evt-hof-1:m-syn-1:g1", gameLabel: "Game 1 of Best-of-3",
      team1: { id: "ALP", name: "Alpha Squad", seriesWL: [5, 0], gameWL: [10, 2], margin: 9.1, price: -180 },
      team2: { id: "BRV", name: "Bravo Crew", seriesWL: [1, 4], gameWL: [3, 9], margin: -5.4, price: 150 },
      sample: "12 games · last 14d", status: "open", clock: "closes 8:00 PM CT", coverage: "complete",
    },
    {
      cgid: "hof_dunk_api:evt-hof-1:m-syn-2:g1", gameLabel: "Game 1 of Best-of-3 · Dunk",
      team1: { id: "RIM", name: "Rim Rockers", seriesWL: [2, 1], gameWL: null, margin: null, price: null },
      team2: { id: "FLT", name: "Flight Club", seriesWL: [1, 2], gameWL: null, margin: null, price: null },
      sample: "3 games · last 30d", status: "building", clock: "coverage building", coverage: "partial",
    },
  ],
  windows: [
    { match: "Game 1 · Bo3", state: "open", reasons: "start_basis:series_scheduled_at_game1 · open" },
    { match: "Game 1 · Bo3", state: "not_open", reasons: "before_open" },
    { match: "Game 2 · Bo5", state: "unavailable", reasons: "individual_start_unresolved" },
    { match: "Game 3 · Bo5", state: "closed", reasons: "game_already_started" },
    { match: "Game 2 · Bo5", state: "unavailable", reasons: "prior_game_not_complete" },
  ],
};

function Crest({ id, name }) {
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#18181C] font-display text-[12px] font-extrabold text-white">
      {initials(name || id)}
    </span>
  );
}

function PriceChip({ price, fav, selected, onClick }) {
  if (price == null) {
    return <div className="flex h-10 items-center justify-center rounded-xl border border-[#1F1F23] bg-[#101013] text-[13px] text-[#52525b]">—</div>;
  }
  const str = price > 0 ? `+${price}` : `${price}`;
  return (
    <button type="button" onClick={onClick}
      className={`flex h-10 items-center justify-center rounded-xl border font-display text-[15px] font-extrabold tabular-nums transition-colors ${
        selected ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]"
          : `bg-[#141417] text-white hover:border-[#00D632]/70 ${fav ? "border-[#00D632]/60" : "border-[#27272A]"}`}`}>
      {str}
    </button>
  );
}

function RecordRow({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between py-[5px] text-[13px]">
      <span className="text-[#71717A]">{label} {sub && <span className="text-[11px]">{sub}</span>}</span>
      <span className="font-semibold text-[#E4E4E7]">{value}</span>
    </div>
  );
}

function windowPillClass(state) {
  if (state === "open") return "bg-[#00D63224] text-[#00D632]";
  if (state === "unavailable") return "bg-[#EAB30824] text-[#EAB308]";
  return "bg-[#26262B] text-[#A1A1AA]";
}

function MatchupCard({ m, onSelect, selection }) {
  const favTeam = (m.team1.price != null && m.team2.price != null)
    ? (m.team1.price <= m.team2.price ? 1 : 2) : 0;
  const record = (t) => (
    <div className="flex-1">
      <div className="mb-1 flex items-center gap-2">
        <Crest id={t.id} name={t.name} />
        <span className="text-[11px] uppercase tracking-wide text-[#71717A]">{t.name}</span>
      </div>
      <RecordRow label="Series record" sub="best-of sets" value={t.seriesWL ? `${t.seriesWL[0]}–${t.seriesWL[1]}` : "—"} />
      <RecordRow label="Game record" sub="individual games" value={t.gameWL ? `${t.gameWL[0]}–${t.gameWL[1]}` : "—"} />
      <RecordRow label="Avg game margin" value={t.margin != null ? (t.margin > 0 ? `+${t.margin}` : `${t.margin}`) : "—"} />
    </div>
  );
  const isSel = (tid) => selection && selection.cgid === m.cgid && selection.teamId === tid;
  return (
    <div className="mb-3 rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] text-[#71717A]">{m.gameLabel}</span>
        {m.status === "open"
          ? <SBTag>Betting open · {m.clock}</SBTag>
          : <span className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase ${windowPillClass("unavailable")}`}>{m.clock}</span>}
      </div>
      <div className="mb-3 grid grid-cols-[1fr_84px] items-center gap-3">
        <div className="flex items-center gap-2"><Crest id={m.team1.id} name={m.team1.name} /><span className="font-semibold text-white">{m.team1.name}</span></div>
        <PriceChip price={m.team1.price} fav={favTeam === 1} selected={isSel(m.team1.id)}
          onClick={() => onSelect(m, m.team1)} />
        <div className="flex items-center gap-2"><Crest id={m.team2.id} name={m.team2.name} /><span className="font-semibold text-white">{m.team2.name}</span></div>
        <PriceChip price={m.team2.price} fav={favTeam === 2} selected={isSel(m.team2.id)}
          onClick={() => onSelect(m, m.team2)} />
      </div>
      <div className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#71717A]">
          Team form — {m.coverage === "complete" ? "this event" : "partial data"} · {m.sample}
        </div>
        <div className="flex gap-4">{record(m.team1)}{record(m.team2)}</div>
        <div className="mt-2 rounded-lg border border-[#EAB30840] bg-[#EAB30814] px-3 py-2 text-[12px] text-[#EAB308]">
          Series and game records are different numbers and are labelled separately. No confirmed-lineup is shown — HOF/Dunk price from team form.
        </div>
      </div>
    </div>
  );
}

function BetSlip({ selection, onClear }) {
  const [stake, setStake] = useState("10");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!selection) {
    return (
      <div className="rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-4 text-[13px] text-[#71717A]">
        Select a price to build your slip. Pregame singles only.
      </div>
    );
  }
  const place = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/sportsbook-upa/wager", {
        command_id: `slip-${Date.now()}`, user_id: DEMO_USER,
        canonical_game_id: selection.cgid, selection_team_id: selection.teamId,
        stake_minor: Math.round(parseFloat(stake || "0") * 100),
      });
      setMsg({ ok: true, text: `Placed · balance ${(data.balance_minor / 100).toFixed(2)}` });
    } catch (e) {
      const d = e?.response?.data;
      setMsg({ ok: false, text: d?.error || "Acceptance is disabled in preview" });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#00D632]/40 bg-[#00D6320a] p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#71717A]">Bet slip</span>
        <button onClick={onClear} className="text-[11px] text-[#71717A] hover:text-white">clear</button>
      </div>
      <div className="mb-2 text-[13px] text-white">{selection.teamName} <span className="font-display font-extrabold">{selection.price > 0 ? `+${selection.price}` : selection.price}</span></div>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[12px] text-[#71717A]">Stake</span>
        <input value={stake} onChange={(e) => setStake(e.target.value)} inputMode="decimal"
          className="h-9 w-24 rounded-lg border border-[#27272A] bg-[#141417] px-2 text-right text-white" />
      </div>
      <button onClick={place} disabled={busy}
        className="h-10 w-full rounded-xl bg-[#00D632] font-display font-extrabold text-[#0A0A0A] disabled:opacity-50">
        {busy ? "Placing…" : "Place wager"}
      </button>
      {msg && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-[12px] ${msg.ok ? "bg-[#00D63214] text-[#00D632]" : "bg-[#EAB30814] text-[#EAB308]"}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function MyBets({ bets }) {
  return (
    <div className="rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-4">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#71717A]">My Bets</div>
      {(!bets || bets.length === 0) ? (
        <div className="text-[13px] text-[#71717A]">No wagers yet.</div>
      ) : (
        <div className="grid gap-2">
          {bets.map((b) => (
            <div key={b.ticket_id} className="flex items-center justify-between rounded-xl border border-[#1F1F23] bg-[#111114] px-3 py-2 text-[13px]">
              <span className="text-white">{b.selection_team_id} <span className="font-display font-extrabold">{b.price > 0 ? `+${b.price}` : b.price}</span></span>
              <span className="text-[#A1A1AA]">${(b.stake_minor / 100).toFixed(2)}</span>
              <span className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase ${b.status === "won" ? "bg-[#00D63224] text-[#00D632]" : b.status === "lost" ? "bg-[#26262B] text-[#A1A1AA]" : "bg-[#5AADDF24] text-[#5AADDF]"}`}>{b.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SportsbookUPA() {
  const [data, setData] = useState(SYNTHETIC);
  const [selection, setSelection] = useState(null);
  const [myBets, setMyBets] = useState([]);

  const refreshBets = useCallback(async () => {
    try {
      const { data: d } = await api.get("/sportsbook-upa/my-bets", { params: { user_id: DEMO_USER } });
      setMyBets(d.bets || []);
    } catch (_e) { /* backend off → keep empty */ }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const recRes = await api.get(`/sportsbook-upa/hof/events/${EVENT_ID}/records`);
        if (live && (recRes?.data?.records || []).length) setData((d) => ({ ...d, synthetic: false }));
      } catch (_e) { /* keep synthetic */ }
      refreshBets();
    })();
    return () => { live = false; };
  }, [refreshBets]);

  const onSelect = (m, team) =>
    setSelection({ cgid: m.cgid, teamId: team.id, teamName: team.name, price: team.price });

  return (
    <Shell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-center gap-3">
          <h1 className="font-display text-2xl font-extrabold text-white">Pro-Am Basketball</h1>
          <span className="rounded-md border border-[#1F1F23] bg-[#18181C] px-2 py-0.5 text-[11px] font-bold text-[#A1A1AA]">Esports · UPA · HOF · Dunk</span>
          {data.synthetic && (
            <span className="rounded-md border border-[#1F1F23] bg-[#18181C] px-2 py-0.5 text-[11px] font-bold text-[#A1A1AA]">SYNTHETIC · PREVIEW</span>
          )}
        </div>
        <p className="mb-5 text-[13px] text-[#71717A]">{data.eventTitle} — team-based matchups. Pregame-only. Confirmed lineups are UPA-only; HOF/Dunk price from team form.</p>

        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="grid gap-4 md:grid-cols-2">
              {data.matchups.map((m) => (
                <MatchupCard key={m.cgid} m={m} onSelect={onSelect} selection={selection} />
              ))}
            </div>
            <div className="mt-5 rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-4">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#71717A]">Pregame-window states</div>
              <p className="mb-3 text-[12px] text-[#71717A]">A match id + game number alone does not open a game — a resolved per-game start, an unstarted game, and a completed prior game are all required.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {data.windows.map((w, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-[#1F1F23] bg-[#111114] px-3 py-2">
                    <div>
                      <div className="text-[13px] font-semibold text-white">{w.match}</div>
                      <div className="font-mono text-[11px] text-[#71717A]">reasons: {w.reasons}</div>
                    </div>
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase ${windowPillClass(w.state)}`}>{w.state.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-4 self-start">
            <BetSlip selection={selection} onClear={() => setSelection(null)} />
            <MyBets bets={myBets} />
          </div>
        </div>
      </div>
    </Shell>
  );
}
